// src/hooks/useMagnetic.js
import { useEffect, useRef } from 'react';
import { useReducedMotion } from './useReducedMotion';

/**
 * Attaches cursor-follow translation to a ref.
 * @param {number} strength - max translation in px
 * @param {boolean} disabled - disable on touch / reduced-motion
 */
export function useMagnetic({ strength = 4, disabled = false } = {}) {
  const ref = useRef(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (disabled || reduced) return;
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(hover: none)').matches) return;

    let rafId = 0;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = ((e.clientX - cx) / rect.width) * strength * 2;
      const dy = ((e.clientY - cy) / rect.height) * strength * 2;
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        el.style.transform = `translate3d(${dx}px, ${dy}px, 0)`;
      });
    };

    const onLeave = () => {
      cancelAnimationFrame(rafId);
      el.style.transform = 'translate3d(0, 0, 0)';
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafId);
    };
  }, [strength, disabled, reduced]);

  return ref;
}
